import React, { useState, useEffect } from 'react';
import CookieConsent from 'react-cookie-consent';
import { Box, Typography, Link } from '@mui/material';

const CookieBanner = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [shouldShowBanner, setShouldShowBanner] = useState(false);

  // Vérifier les préférences existantes au chargement
  useEffect(() => {
    const shouldDisplayBanner = checkExistingConsent();
    setShouldShowBanner(shouldDisplayBanner);
    setInitialized(true);
  }, []);

  const checkExistingConsent = () => {
    const cookies = document.cookie.split(';');
    
    // 1. Vérifier si la bannière a déjà été affichée (cookie de la bibliothèque)
    const bannerCookie = cookies.find(c => c.trim().startsWith('cookie_consent_displayed='));
    
    // 2. Vérifier le consentement utilisateur (votre cookie)
    const consentCookie = cookies.find(c => c.trim().startsWith('cookie_consent='));
    
    // Si la bannière a déjà été affichée (cookie existe), ne pas la réafficher
    if (bannerCookie) {
      return false;
    }
    
    // Si l'utilisateur n'a pas encore donné son consentement OU si le cookie a expiré
    if (!consentCookie) {
      return true;
    }
    
    // Récupérer la valeur du consentement
    const hasConsent = consentCookie.split('=')[1] === 'true';
    
    // Mettre à jour Google Analytics si présent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: hasConsent ? 'granted' : 'denied'
      });
    }
    
    // Stocker dans localStorage pour cohérence
    localStorage.setItem('cookiePreferences', JSON.stringify({ 
      essential: true, 
      analytics: hasConsent,
      consentDate: new Date().toISOString()
    }));
    
    // Ne pas afficher la bannière si consentement déjà donné
    return false;
  };

  const handleAccept = () => {
    // Cookies essentiels (toujours présents)
    document.cookie = "cookie_essential=true; path=/; max-age=31536000; SameSite=Lax";
    
    // Consentement pour les cookies non-essentiels
    document.cookie = "cookie_consent=true; path=/; max-age=31536000; SameSite=Lax";
    document.cookie = "cookie_preferences=true; path=/; max-age=31536000; SameSite=Lax";
    document.cookie = "cookie_analytics=true; path=/; max-age=31536000; SameSite=Lax";

    // Mettre à jour Google Analytics si présent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    }

    // Stocker dans localStorage pour une gestion facile côté client
    localStorage.setItem('cookiePreferences', JSON.stringify({ 
      essential: true, 
      preferences: true,
      analytics: true,
      consentDate: new Date().toISOString()
    }));
    
    // IMPORTANT: Mettre à jour le flag de bannière affichée
    localStorage.setItem('cookiesAccepted', 'true');

    // Prévenir la bulle de chat pour qu'elle redescende à sa position normale
    window.dispatchEvent(new Event('vs-cookie-consent'));
  };

  const handleDecline = () => {
    // Cookies essentiels seulement (nécessaires au fonctionnement)
    document.cookie = "cookie_essential=true; path=/; max-age=31536000; SameSite=Lax";
    
    // Refus des autres cookies
    document.cookie = "cookie_consent=false; path=/; max-age=31536000; SameSite=Lax";
    document.cookie = "cookie_preferences=false; path=/; max-age=31536000; SameSite=Lax";
    document.cookie = "cookie_analytics=false; path=/; max-age=31536000; SameSite=Lax";

    // Bloquer Google Analytics si présent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      });
    }

    // Stocker le refus dans localStorage
    localStorage.setItem('cookiePreferences', JSON.stringify({ 
      essential: true, 
      preferences: false,
      analytics: false,
      consentDate: new Date().toISOString()
    }));
    
    // IMPORTANT: Mettre à jour le flag de bannière affichée
    localStorage.setItem('cookiesAccepted', 'false');

    // Prévenir la bulle de chat pour qu'elle redescende à sa position normale
    window.dispatchEvent(new Event('vs-cookie-consent'));
  };

  // Ne pas afficher le bannier si déjà initialisé et pas besoin d'afficher
  if (!initialized || !shouldShowBanner) return null;

  return (
    <CookieConsent
      location="bottom"
      buttonText="J'accepte"
      declineButtonText="Je refuse"
      cookieName="cookie_consent_displayed" // Cookie pour masquer la bannière
      style={{ 
        background: "#f5f5f5", 
        color: "#333",
        alignItems: "center",
        padding: "16px 24px",
        fontSize: "14px"
      }}
      buttonStyle={{ 
        background: "#c62828", 
        color: "white", 
        fontSize: "14px",
        borderRadius: "4px",
        padding: "8px 16px",
        fontWeight: "bold"
      }}
      declineButtonStyle={{ 
        background: "#757575", 
        color: "white", 
        fontSize: "14px",
        borderRadius: "4px",
        padding: "8px 16px",
        marginLeft: "10px"
      }}
      expires={365}
      enableDeclineButton
      flipButtons={false}
      onAccept={handleAccept}
      onDecline={handleDecline}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" component="span">
          🍪 <strong>Votre confidentialité nous importe</strong><br/>
          Nous utilisons des cookies pour améliorer votre expérience sur notre site.
          <Link 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              setShowDetails(!showDetails);
            }}
            sx={{ 
              ml: 1, 
              textDecoration: 'none', 
              fontWeight: 'bold',
              color: '#c62828',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {showDetails ? 'Masquer les détails' : 'En savoir plus'}
          </Link>
        </Typography>
        
        {showDetails && (
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            backgroundColor: 'white', 
            borderRadius: 1,
            border: '1px solid #e0e0e0'
          }}>
            <Typography variant="body2" color="text.secondary">
              <strong>🍪 Cookies essentiels :</strong> Nécessaires au fonctionnement du site (toujours activés)<br/>
              <strong>⚙️ Cookies de préférences :</strong> Mémorisent vos choix de langue, thème, etc.<br/>
              <strong>📊 Cookies analytiques :</strong> Nous aident à comprendre l'utilisation du site<br/><br/>
              
              <small>
                En cliquant sur "J'accepte", vous consentez à l'utilisation de tous les cookies.<br/>
                En cliquant sur "Je refuse", seuls les cookies essentiels seront utilisés.
              </small>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Pour plus d'informations, consultez notre{' '}
              <Link 
                href="/politique-cookies" 
                sx={{ 
                  textDecoration: 'none', 
                  fontWeight: 'bold',
                  color: '#c62828'
                }}
              >
                politique de cookies
              </Link>.
            </Typography>
          </Box>
        )}
      </Box>
    </CookieConsent>
  );
};

export default CookieBanner;